"use client";

import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import { ShieldCheck } from "lucide-react";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import DatePicker from "../../../../components/DatePicker";
import Table from "../../../../components/Table.jsx";
import Pagination from "../../../../components/Pagination";
import styles from "../css/ImmunizationHistory.module.css";
import MedicalHistoryHeader from "../components/MedicalHistoryHeader";
import { getConsultationHistoryPatient, getPatientConsultationTimeline } from "../../../../api/clinic/consultationApi.js";
import { getProfileByPatient } from "../../../../api/profile/create_profile.js";

export default function ImmunizationHistory() {
  const { id } = useParams();
  const { consultationData } = useConsultation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [immunizations, setImmunizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timeline, setTimeline] = useState([]);
  const [profileData, setProfileData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [patient, setPatient] = useState(consultationData?.patient || null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (consultationData?.patient) {
        if (mounted) setPatient(consultationData.patient);
        return;
      }
      if (!id) return;
      try {
        const p = await getConsultationHistoryPatient(id);
        if (mounted) setPatient(p);

        if (!p) {
          const timeline = await getPatientConsultationTimeline(id);
          const p2 = Array.isArray(timeline) && timeline.length ? timeline[0]?.patient : null;
          if (mounted) setPatient(p2 || null);
        }
      } catch {
        if (mounted) setPatient(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id, consultationData?.patient]);

  useEffect(() => {
    const patientId = id;
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getProfileByPatient(patientId);
        if (!cancelled) setProfileData(data);
      } catch {
        if (!cancelled) setProfileData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const patientFromProfile = profileData?.patient || null;
  const resolvedPatient = {
    ...(patientFromProfile || patient || {}),
    profilenumber:
      profileData?.profile?.profilenumber ??
      patientFromProfile?.profilenumber ??
      patient?.profilenumber,
    medicalaidprovider: profileData?.mainmember?.medicalaidprovider ?? profileData?.mainmember?.medicalAidProvider,
    medicalaidnumber: profileData?.mainmember?.medicalaidnumber ?? profileData?.mainmember?.medicalAidNumber,
  };

  const dateKey = (val) => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-CA");
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!id) return;
      setLoading(true);
      setError("");
      try {
        const t = await getPatientConsultationTimeline(id);
        if (!mounted) return;
        const rows = Array.isArray(t) ? t : [];
        setTimeline(rows);
        if (!selectedDate && rows.length) {
          const d = rows[0]?.consultationdate ? new Date(rows[0].consultationdate) : new Date();
          setSelectedDate(d);
        }
      } catch (e) {
        if (!mounted) return;
        setTimeline([]);
        setError(e?.message || "Failed to load immunization history");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const derivedImmunizations = useMemo(() => {
    const isImmunizationName = (name) => {
      const n = String(name || "").toLowerCase();
      return n.includes("immun") || n.includes("vacc");
    };
    const list = [];
    for (const c of timeline || []) {
      const dt = c?.consultationdate ? new Date(c.consultationdate) : null;
      const dateOnly = dt && !isNaN(dt.getTime()) ? dt.toLocaleDateString("en-CA") : "";

      const primaryName = c?.service?.servicename;
      if (isImmunizationName(primaryName)) {
        list.push({
          id: `${c?.consultationid || ""}-primary`,
          vaccineName: primaryName || "",
          dateAdministered: dateOnly,
          batchNo: "—",
          site: "—",
          administrator: "—",
          nextDue: null,
        });
      }

      for (const w of c?.consultation_service_workflow_data || []) {
        const svcName = w?.service?.servicename;
        if (!isImmunizationName(svcName)) continue;
        list.push({
          id: `${c?.consultationid || ""}-wf-${w?.serviceid || ""}`,
          vaccineName: svcName || "",
          dateAdministered: dateOnly,
          batchNo: "—",
          site: "—",
          administrator: "—",
          nextDue: null,
        });
      }
    }
    return list;
  }, [timeline]);

  const filteredImmunizations = useMemo(() => {
    const targetKey = dateKey(selectedDate);
    if (!targetKey) return derivedImmunizations;
    return (derivedImmunizations || []).filter((r) => r?.dateAdministered === targetKey);
  }, [derivedImmunizations, selectedDate]);

  useEffect(() => {
    setImmunizations(filteredImmunizations);
  }, [filteredImmunizations]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate]);

  // Calculate pagination
  const totalPages = Math.ceil(immunizations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedImmunizations = immunizations.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatNextDue = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString + "-01");
    return date.toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric'
    });
  };

  const renderNextDue = (nextDue) => {
    if (!nextDue) return "—";
    const formattedDate = formatNextDue(nextDue);
    return (
      <span className={styles.nextDueCell}>
        <svg className={styles.calendarIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        {formattedDate}
      </span>
    );
  };

  const tableData = paginatedImmunizations.map(immunization => ({
    vaccineName: immunization.vaccineName,
    dateAdministered: formatDate(immunization.dateAdministered),
    batchNo: (
      <span className={styles.batchNo}>{immunization.batchNo}</span>
    ),
    site: immunization.site,
    administrator: immunization.administrator,
    nextDue: renderNextDue(immunization.nextDue)
  }));

  const columns = [
    { header: "Vaccine", accessor: "vaccineName" },
    { header: "Date", accessor: "dateAdministered" },
    { header: "Batch No.", accessor: "batchNo" },
    { header: "Site", accessor: "site" },
    { header: "Administrator", accessor: "administrator" },
    { header: "Next Due", accessor: "nextDue" }
  ];

  return (
    <div className={styles.pageWrapper}>
      <Navbar />

      <main className={styles.mainContent}>
        <div className={styles.container}>
          <MedicalHistoryHeader title="Immunization History" patient={resolvedPatient} />
         

          {/* Date Selector */}
          <div className={styles.dateSelector}>
            <div className={styles.datePickerContainer}>
              <DatePicker
                selectedDate={selectedDate || new Date()}
                onDateChange={setSelectedDate}
                dateFormat="dd/MM/yyyy"
                placeholder="Select date"
              />
            </div>
          </div>

          {error && <div className={styles.inlineError}>{error}</div>}

          {loading ? (
            <div className={styles.loadingMessage}>Loading immunization records...</div>
          ) : (
            <>
              <div className={styles.tableCard}>
                <div className={styles.tableHeader}>
                  <ShieldCheck size={20} className={styles.headerIcon} />
                  <h3 className={styles.tableTitle}>Immunization History</h3>
                </div>
                <Table columns={columns} data={tableData} />
              </div>
              {totalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
